// admin-panel/src/pages/Blog.jsx
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { blogApi } from '../lib/api';
import {
  Plus, Edit2, Trash2, Search, Eye, Tag, ChevronLeft, ChevronRight,
  BookOpen, Star, Clock, CheckCircle2, AlertCircle, Filter, X, Settings2,
  Save, TrendingUp
} from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_META = {
  published: { label: 'Published', cls: 'bg-green-500/10 text-green-400 border-green-500/20' },
  draft:     { label: 'Draft',     cls: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' },
  scheduled: { label: 'Scheduled', cls: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
};

const EMPTY_CAT = { name: '', description: '', sort_order: 0, is_active: true };

export default function Blog() {
  const qc = useQueryClient();

  // Filters & pagination
  const [search, setSearch]     = useState('');
  const [status, setStatus]     = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [page, setPage]         = useState(1);

  // Category management modal
  const [catModal, setCatModal]   = useState(false);
  const [catForm, setCatForm]     = useState(EMPTY_CAT);
  const [editingCat, setEditingCat] = useState(null);

  // Confirm delete state
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  /* ── Queries ── */
  const { data: postsData, isLoading } = useQuery({
    queryKey: ['admin-blog', { search, status, catFilter, page }],
    queryFn: () => blogApi.list({
      search:   search   || undefined,
      status:   status   || undefined,
      category: catFilter || undefined,
      page,
      per_page: 12,
    }),
    keepPreviousData: true,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['blog-categories-public'],
    queryFn: blogApi.listCategories,
  });

  const posts     = postsData?.data || [];
  const total     = postsData?.total || 0;
  const lastPage  = postsData?.last_page || 1;
  const catList   = Array.isArray(categories) ? categories : (categories?.data || []);

  /* ── Mutations ── */
  const deleteMut = useMutation({
    mutationFn: blogApi.delete,
    onSuccess: () => { qc.invalidateQueries(['admin-blog']); toast.success('Post deleted'); setDeleteConfirm(null); },
    onError:   (e) => toast.error(e.message || 'Delete failed'),
  });

  const saveCatMut = useMutation({
    mutationFn: (d) => editingCat ? blogApi.updateCategory(editingCat.id, d) : blogApi.createCategory(d),
    onSuccess: () => { qc.invalidateQueries(['blog-categories-public']); closeCatForm(); toast.success('Category saved'); },
    onError:   (e) => toast.error(e.message || 'Save failed'),
  });

  const deleteCatMut = useMutation({
    mutationFn: blogApi.deleteCategory,
    onSuccess: () => { qc.invalidateQueries(['blog-categories-public']); toast.success('Category deleted'); },
    onError:   (e) => toast.error(e.message || 'Delete failed'),
  });

  function openCatEdit(cat) { setEditingCat(cat); setCatForm({ name: cat.name, description: cat.description || '', sort_order: cat.sort_order || 0, is_active: cat.is_active ?? true }); }
  function closeCatForm() { setEditingCat(null); setCatForm(EMPTY_CAT); }

  function applySearch(e) { e.preventDefault(); setPage(1); }

  /* ── Stats counts ── */
  const countPublished = posts.filter(p => p.status === 'published').length;

  return (
    <div className="space-y-5">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-white">Blog Management</h1>
          <p className="text-xs text-[#555] mt-0.5">{total} post{total !== 1 ? 's' : ''} total</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setCatModal(true)}
            className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm font-medium px-4 py-2.5 rounded-xl transition-all"
          >
            <Tag size={14} /> Categories
          </button>
          <Link
            to="/blog/new"
            className="flex items-center gap-2 bg-gradient-to-r from-[#8B5E3C] to-[#D4AF37] text-white text-sm font-semibold px-4 py-2.5 rounded-xl hover:opacity-90 transition-all"
          >
            <Plus size={16} /> New Post
          </Link>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="bg-[#151515] border border-white/5 rounded-xl p-4">
        <form onSubmit={applySearch} className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#444]" />
            <input
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search posts…"
              className="w-full bg-[#121212] border border-white/5 rounded-xl pl-8 pr-3 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30"
            />
            {search && <button type="button" onClick={() => { setSearch(''); setPage(1); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#555] hover:text-white"><X size={12} /></button>}
          </div>
          <select
            value={status}
            onChange={e => { setStatus(e.target.value); setPage(1); }}
            className="bg-[#121212] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#D4AF37]/30"
          >
            <option value="">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="scheduled">Scheduled</option>
          </select>
          <select
            value={catFilter}
            onChange={e => { setCatFilter(e.target.value); setPage(1); }}
            className="bg-[#121212] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#D4AF37]/30"
          >
            <option value="">All Categories</option>
            {catList.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </form>
      </div>

      {/* ── Posts Grid ── */}
      {isLoading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array(6).fill(0).map((_, i) => (
            <div key={i} className="h-52 rounded-xl bg-white/5 animate-pulse" />
          ))}
        </div>
      ) : posts.length === 0 ? (
        <div className="bg-[#151515] border border-white/5 rounded-xl py-16 flex flex-col items-center gap-3">
          <BookOpen size={36} className="text-[#333]" />
          <p className="text-[#555] text-sm">No posts found</p>
          <Link to="/blog/new" className="text-[#D4AF37] text-sm hover:underline flex items-center gap-1"><Plus size={14} /> Create your first post</Link>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {posts.map(post => {
            const sm = STATUS_META[post.status] || STATUS_META.draft;
            return (
              <div key={post.id} className="bg-[#151515] border border-white/5 rounded-xl overflow-hidden hover:border-[#D4AF37]/20 transition-all group flex flex-col">
                {/* Thumbnail */}
                <div className="relative h-36 bg-[#1A1A1A] overflow-hidden flex-shrink-0">
                  {post.featured_image ? (
                    <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <BookOpen size={28} className="text-[#2A2A2A]" />
                    </div>
                  )}
                  <div className="absolute top-2 left-2 flex gap-1">
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${sm.cls}`}>{sm.label}</span>
                    {post.is_featured && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20 flex items-center gap-0.5"><Star size={9} /> Featured</span>}
                  </div>
                  {/* Actions */}
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Link to={`/blog/${post.id}/edit`} className="w-7 h-7 rounded-lg bg-black/60 backdrop-blur-sm flex items-center justify-center text-white hover:bg-[#D4AF37]/20 hover:text-[#D4AF37] transition-all">
                      <Edit2 size={12} />
                    </Link>
                    <button
                      onClick={() => setDeleteConfirm(post)}
                      className="w-7 h-7 rounded-lg bg-black/60 backdrop-blur-sm flex items-center justify-center text-white hover:bg-red-500/20 hover:text-red-400 transition-all"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 flex flex-col flex-1">
                  {post.category && (
                    <span className="text-[10px] uppercase tracking-wider text-[#D4AF37] font-semibold mb-1">{post.category.name}</span>
                  )}
                  <h3 className="text-white text-sm font-semibold leading-snug line-clamp-2 mb-1">{post.title}</h3>
                  {post.excerpt && <p className="text-[#666] text-xs line-clamp-2 mb-2">{post.excerpt}</p>}
                  <div className="mt-auto flex items-center justify-between pt-2 border-t border-white/5">
                    <div className="flex items-center gap-2 text-[#555] text-[11px]">
                      <Eye size={11} /> {post.views_count || 0} views
                    </div>
                    <span className="text-[#555] text-[11px]">
                      {post.published_at ? post.published_at.split(' ')[0] : post.created_at}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Pagination ── */}
      {lastPage > 1 && (
        <div className="flex items-center justify-between bg-[#151515] border border-white/5 rounded-xl px-4 py-3">
          <span className="text-xs text-[#555]">Page {page} of {lastPage} · {total} posts</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-30 flex items-center justify-center text-[#888] transition-all"
            >
              <ChevronLeft size={14} />
            </button>
            {Array.from({ length: Math.min(5, lastPage) }, (_, i) => {
              const p = page <= 3 ? i + 1 : page + i - 2;
              if (p < 1 || p > lastPage) return null;
              return (
                <button key={p} onClick={() => setPage(p)}
                  className={`w-8 h-8 rounded-lg text-xs font-medium transition-all ${p === page ? 'bg-[#D4AF37] text-black' : 'bg-white/5 hover:bg-white/10 text-[#888]'}`}
                >{p}</button>
              );
            })}
            <button
              onClick={() => setPage(p => Math.min(lastPage, p + 1))}
              disabled={page === lastPage}
              className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-30 flex items-center justify-center text-[#888] transition-all"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* ── Delete Confirm Modal ── */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-6 max-w-sm w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
                <AlertCircle size={18} className="text-red-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm">Delete Post?</h3>
                <p className="text-[#888] text-xs mt-0.5">This action cannot be undone.</p>
              </div>
            </div>
            <p className="text-[#666] text-sm mb-5 line-clamp-2">"{deleteConfirm.title}"</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 rounded-xl text-sm text-[#888] bg-white/5 hover:bg-white/10 transition-all">Cancel</button>
              <button
                onClick={() => deleteMut.mutate(deleteConfirm.id)}
                disabled={deleteMut.isPending}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-red-500/80 hover:bg-red-500 disabled:opacity-50 transition-all"
              >
                {deleteMut.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Category Management Modal ── */}
      {catModal && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl max-w-lg w-full max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-white/5">
              <h3 className="text-white font-semibold">Blog Categories</h3>
              <button onClick={() => { setCatModal(false); closeCatForm(); }}><X size={18} className="text-[#888] hover:text-white" /></button>
            </div>

            {/* Add / Edit form */}
            <div className="p-5 border-b border-white/5">
              <p className="text-xs text-[#555] mb-3">{editingCat ? `Editing: ${editingCat.name}` : 'New Category'}</p>
              <div className="space-y-3">
                <input
                  value={catForm.name}
                  onChange={e => setCatForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Category name *"
                  className="w-full bg-[#121212] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30"
                />
                <input
                  value={catForm.description}
                  onChange={e => setCatForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Description (optional)"
                  className="w-full bg-[#121212] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30"
                />
                <div className="flex gap-3 items-center">
                  <input
                    type="number"
                    value={catForm.sort_order}
                    onChange={e => setCatForm(f => ({ ...f, sort_order: +e.target.value }))}
                    placeholder="Sort order"
                    className="w-28 bg-[#121212] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30"
                  />
                  <label className="flex items-center gap-2 cursor-pointer">
                    <div onClick={() => setCatForm(f => ({ ...f, is_active: !f.is_active }))} className={`w-9 h-5 rounded-full relative transition-all ${catForm.is_active ? 'bg-[#D4AF37]' : 'bg-white/10'}`}>
                      <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${catForm.is_active ? 'left-4' : 'left-0.5'}`} />
                    </div>
                    <span className="text-sm text-[#888]">Active</span>
                  </label>
                  <div className="ml-auto flex gap-2">
                    {editingCat && <button onClick={closeCatForm} className="px-3 py-2 text-xs rounded-lg bg-white/5 text-[#888] hover:text-white">Cancel</button>}
                    <button
                      onClick={() => saveCatMut.mutate(catForm)}
                      disabled={!catForm.name || saveCatMut.isPending}
                      className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-lg bg-gradient-to-r from-[#8B5E3C] to-[#D4AF37] text-white font-semibold disabled:opacity-50"
                    >
                      <Save size={11} />{saveCatMut.isPending ? 'Saving…' : editingCat ? 'Update' : 'Add'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Category list */}
            <div className="overflow-y-auto flex-1 p-5 space-y-2">
              {catList.length === 0 ? (
                <p className="text-[#555] text-sm text-center py-6">No categories yet</p>
              ) : catList.map(cat => (
                <div key={cat.id} className="flex items-center gap-3 p-3 bg-[#121212] rounded-xl group">
                  <div className={`w-2 h-2 rounded-full ${cat.is_active ? 'bg-green-400' : 'bg-[#444]'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white font-medium">{cat.name}</div>
                    {cat.description && <div className="text-xs text-[#555] truncate">{cat.description}</div>}
                  </div>
                  <span className="text-[11px] text-[#444]">{cat.posts_count ?? 0} posts</span>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => openCatEdit(cat)} className="p-1.5 rounded-lg hover:bg-white/10 text-[#888] hover:text-white"><Edit2 size={12} /></button>
                    <button onClick={() => { if (window.confirm(`Delete "${cat.name}"?`)) deleteCatMut.mutate(cat.id); }} className="p-1.5 rounded-lg hover:bg-red-500/10 text-[#888] hover:text-red-400"><Trash2 size={12} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
